/**
 * Pushes "on sale" product cards to the very bottom of the collection and
 * keeps them visually hidden until the shopper has scrolled through every
 * non-sale product — so a normal scroll through the grid never surfaces a
 * sale card at all.
 *
 * v4 — fixes a bug from v3: on a collection that is entirely (or almost
 * entirely) sale items — e.g. the dedicated "SALE" collection — v3 hid
 * every single card until the very last page, which made the page look
 * empty. v4 adds a safety valve: if an entire loaded batch turns out to be
 * 100% sale with no non-sale items seen at all, hiding is abandoned for
 * that grid and everything is shown normally right away.
 */
(() => {
  const GRID_SELECTOR = 'ul[ref="grid"]';

  class SaleSort {
    constructor(grid) {
      this.grid = grid;
      this.processed = new WeakSet();
      this.firstSaleNode = null;
      this.hiddenSaleNodes = [];
      this.nonSaleSeen = 0;

      const declaredLastPage = Number(grid.getAttribute('last-page'));
      this.lastPage = Number.isFinite(declaredLastPage) && declaredLastPage > 0 ? declaredLastPage : null;
      // If we can't determine the total page count, don't hide anything —
      // fall back to just keeping sale cards pinned at the bottom.
      this.revealed = this.lastPage === null;

      this.observer = new MutationObserver((mutations) => this.#handleMutations(mutations));
      this.observer.observe(grid, { childList: true });

      this.#processNodes(Array.from(grid.children));
    }

    #handleMutations(mutations) {
      const added = [];
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && !this.processed.has(node)) {
            added.push(node);
          }
        }
      }
      if (added.length === 0) return;
      this.#processNodes(added);
    }

    #processNodes(nodes) {
      // Re-validate the sale-zone anchor in case the grid was cleared/rebuilt
      // (e.g. a filter change re-rendered the list).
      if (this.firstSaleNode && !this.firstSaleNode.isConnected) {
        this.firstSaleNode = this.grid.querySelector('[data-on-sale="true"]');
      }

      let sawNonSaleThisBatch = false;

      for (const node of nodes) {
        if (this.processed.has(node)) continue;
        this.processed.add(node);
        if (!node.getAttribute) continue;

        const isSale = node.getAttribute('data-on-sale') === 'true';

        if (isSale) {
          this.grid.appendChild(node);
          if (!this.firstSaleNode) this.firstSaleNode = node;
          if (!this.revealed) {
            node.style.display = 'none';
            node.setAttribute('data-sale-pending', '');
            this.hiddenSaleNodes.push(node);
          }
        } else {
          sawNonSaleThisBatch = true;
          this.nonSaleSeen++;
          if (this.firstSaleNode) {
            this.grid.insertBefore(node, this.firstSaleNode);
          }
        }
      }

      // Safety valve: an entire batch (most importantly, the whole first page)
      // that is 100% sale with nothing else at all almost certainly means this
      // is a dedicated Sale collection (or fully discounted), not a normal
      // mixed one. Hiding everything until the last page would make the page
      // look empty, so stop hiding and show everything normally right away.
      if (!this.revealed && !sawNonSaleThisBatch && this.nonSaleSeen === 0 && this.hiddenSaleNodes.length > 0) {
        this.#reveal();
        return;
      }

      this.#maybeReveal();
    }

    #maybeReveal() {
      if (this.revealed || !this.lastPage) return;

      let maxPage = 0;
      for (const li of this.grid.querySelectorAll('[data-page]')) {
        const page = Number(li.getAttribute('data-page'));
        if (page > maxPage) maxPage = page;
      }

      if (maxPage < this.lastPage) return;
      this.#reveal();
    }

    #reveal() {
      this.revealed = true;
      for (const node of this.hiddenSaleNodes) {
        node.style.removeProperty('display');
        node.removeAttribute('data-sale-pending');
      }
      this.hiddenSaleNodes = [];
    }
  }

  const attached = new WeakSet();

  function attach(grid) {
    if (attached.has(grid)) return;
    attached.add(grid);
    new SaleSort(grid);
  }

  function scan(root) {
    root.querySelectorAll(GRID_SELECTOR).forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }

  // Grids added later (client-side navigation / filter re-renders)
  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches && node.matches(GRID_SELECTOR)) attach(node);
        if (node.querySelectorAll) scan(node);
      }
    }
  });

  bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
